import { memo, Fragment, ReactNode, useState } from 'react';

import clsx from 'clsx';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { isEmpty, isFunction, isNullish } from 'remeda';

import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';

import css from './DataTable.module.css';

import { EmptyPlaceholder } from 'v2/components/emptyPlaceholder/EmptyPlaceholder';
import { TableColumn, WithClassName } from 'v2/types';

export interface DataTableProps<T> extends WithClassName {
  containerClassName?: string;
  columns: TableColumn<T>[];
  data?: T[];
  withPagination?: boolean;
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  renderExpandedRow?: (row: T) => ReactNode;
  getRowId?: (row: T) => string;
}

const _DataTable = <T extends Record<string, any>>({
  className,
  containerClassName,
  columns,
  data,
  withPagination,
  total,
  page,
  pageSize,
  onPageChange,
  renderExpandedRow,
  getRowId,
}: DataTableProps<T>) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (isNullish(data)) {
    return null;
  }

  const handlePageChange = (_: unknown, page: number) => {
    onPageChange?.(page + 1);
  };

  const resolveId = (row: T): string => (isFunction(getRowId) ? getRowId(row) : row.id);

  const handleToggleExpand = (row: T) => {
    const id = resolveId(row);
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const isExpandable = isFunction(renderExpandedRow);
  const colSpan = columns.length + (isExpandable ? 1 : 0);

  return (
    <div className={clsx(css.tableWrapper, containerClassName)}>
      <TableContainer className={clsx(css.tableContainer, className)}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {isExpandable && <TableCell className={css.tableHead} style={{ width: 40 }} />}
              {columns.map(({ key, align, label, style }) => (
                <TableCell key={key} className={css.tableHead} align={align} style={style}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {isEmpty(data) ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center">
                  <EmptyPlaceholder />
                </TableCell>
              </TableRow>
            ) : (
              <Fragment>
                {data.map((row) => {
                  const rowId = resolveId(row);
                  const isExpanded = expandedId === rowId;

                  return (
                    <Fragment key={rowId}>
                      <TableRow
                        hover
                        className={clsx(isExpandable && css.expandableRow, isExpanded && css.expandedRow)}
                        onClick={isExpandable ? () => handleToggleExpand(row) : undefined}
                      >
                        {isExpandable && (
                          <TableCell className={css.expandChevronCell} padding="none">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleExpand(row);
                              }}
                            >
                              {isExpanded ? (
                                <ChevronDown width={16} height={16} />
                              ) : (
                                <ChevronRight width={16} height={16} />
                              )}
                            </IconButton>
                          </TableCell>
                        )}
                        {columns.map(({ key, align, render, id, ellipsis }) => (
                          <TableCell key={key} className={clsx(ellipsis && 'truncate')} align={align}>
                            {isFunction(render) ? render(row) : row[id ?? '']}
                          </TableCell>
                        ))}
                      </TableRow>

                      {isExpandable && (
                        <TableRow className={css.expandedContentRow}>
                          <TableCell colSpan={colSpan} className={css.expandedContentCell} padding="none">
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              {renderExpandedRow(row)}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </Fragment>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {withPagination && (
        <TablePagination
          className={css.tablePagination}
          component="div"
          count={total ?? 0}
          rowsPerPage={pageSize ?? 0}
          page={(page ?? 0) - 1}
          onPageChange={handlePageChange}
          rowsPerPageOptions={[]}
        />
      )}
    </div>
  );
};

export const DataTable = memo(_DataTable) as typeof _DataTable;
